import React, { useState, useEffect } from "react";
import Footer from "../Components/Footer";
import IndiaBoatsTopBar from "../Components/IndiaBoatsTopBar";
import PremiumNavbar from "../Components/PremiumNavbar";
import AuthPrompt from "../Components/AuthPrompt";
import "./profile.css";
import {
  User,
  Anchor,
  MapPin,
  Calendar,
  History,
  Mail,
  Phone,
  ChevronDown,
} from "lucide-react";

const API_URL = "/api";

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch (error) {
    return null;
  }
};

const formatDisplayDate = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const Profile = () => {
  const storedUser = getStoredUser();
  const [userData, setUserData] = useState(storedUser);
  const [formData, setFormData] = useState(storedUser || {});
  const [voyages, setVoyages] = useState([]);
  const [expandedVoyageId, setExpandedVoyageId] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(Boolean(storedUser));
  const [showAuthPrompt, setShowAuthPrompt] = useState(!storedUser);

  useEffect(() => {
    const localUser = getStoredUser();
    if (!localUser || !(localUser._id || localUser.id)) {
      setShowAuthPrompt(true);
      setLoading(false);
      return;
    }

    const userId = localUser._id || localUser.id;

    const loadProfile = async () => {
      try {
        const [profileResponse, bookingsResponse] = await Promise.all([
          fetch(`${API_URL}/signup/${userId}`),
          fetch(`${API_URL}/bookings/user/${userId}`),
        ]);

        if (profileResponse.ok) {
          const profile = await profileResponse.json();
          setUserData(profile);
          setFormData(profile);
          localStorage.setItem("user", JSON.stringify(profile));
        } else {
          setUserData(localUser);
          setFormData(localUser);
        }

        if (bookingsResponse.ok) {
          const bookings = await bookingsResponse.json();
          const voyageRows = (Array.isArray(bookings) ? bookings : []).map(
            (booking) => {
              const details = Array.isArray(booking.bookingDetails)
                ? booking.bookingDetails
                : [];
              const addons = Array.isArray(booking.addons)
                ? booking.addons
                : [];
              const boatNames = details
                .map((item) => item.boatName)
                .filter(Boolean);

              return {
                id: String(booking._id),
                boat: boatNames.join(", ") || "Booking",
                date: formatDisplayDate(
                  details[0]?.bookingDate || booking.createdAt
                ),
                details,
                addons,
                totalAmount: Number(booking.totalAmount) || 0,
                taxAmount: Number(booking.taxAmount) || 0,
                addonTotal: Number(booking.addonTotal) || 0,
                finalAmount: Number(booking.finalAmount) || 0,
                createdAt: formatDisplayDate(booking.createdAt),
              };
            }
          );

          setVoyages(voyageRows);
        }
      } catch (error) {
        console.error(error);
        setUserData(localUser);
        setFormData(localUser);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  const handleEditClick = () => setIsEditing(true);

  const handleCancel = () => {
    setFormData(userData);
    setIsEditing(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    const mobile = String(formData.mobile || formData.mobileNumber || "").replace(
      /\D/g,
      ""
    );

    if (!String(formData.name || "").trim()) {
      alert("Name is required");
      return;
    }

    if (!/^\d{10}$/.test(mobile)) {
      alert("Please enter valid 10-digit mobile number");
      return;
    }

    const userId = userData._id || userData.id;
    setSaving(true);

    try {
      const response = await fetch(`${API_URL}/signup/${userId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          mobile,
          companyName: formData.companyName || "",
          gst: formData.gst || "",
          address: formData.address || formData.companyAddress || "",
          reference: formData.reference || "",
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        alert(result.message || "Update failed");
        return;
      }

      setUserData(result.data);
      setFormData(result.data);
      localStorage.setItem("user", JSON.stringify(result.data));
      setIsEditing(false);
    } catch (err) {
      console.error(err);
      alert("Update failed");
    } finally {
      setSaving(false);
    }
  };

  const joinedDate = formatDisplayDate(userData?.createdAt);
  const displayAddress =
    userData?.address || userData?.companyAddress || "";

  return (
    <>
      <IndiaBoatsTopBar />
      <PremiumNavbar />

      <div className="ib_profile_main_wrapper">
        <div className="ib_profile_content_container">
          {userData && !showAuthPrompt ? (
            <>
              <header className="ib_profile_top_header">
                <div className="ib_profile_header_text_group">
                  <h1 className="ib_profile_main_title">User Profile</h1>
                  <p className="ib_profile_main_subtitle">
                    Manage your account settings and boat booking preferences.
                  </p>
                </div>

                <div className="ib_profile_header_actions">
                  {!isEditing ? (
                    <button
                      className="ib_profile_action_btn"
                      onClick={handleEditClick}
                    >
                      Edit Profile
                    </button>
                  ) : (
                    <>
                      <button
                        className="ib_profile_action_btn"
                        onClick={handleSave}
                        disabled={saving}
                      >
                        {saving ? "Saving..." : "Save"}
                      </button>
                      <button
                        className="ib_profile_action_btn secondary"
                        onClick={handleCancel}
                        disabled={saving}
                      >
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              </header>

              <section className="ib_profile_base_card">
                <div className="ib_profile_inner_body no-avatar">
                  <div className="ib_profile_user_branding">
                    <h2 className="ib_profile_display_name">{userData.name}</h2>

                    <div className="ib_profile_quick_meta">
                      {displayAddress ? (
                        <span className="ib_profile_meta_row">
                          <MapPin size={14} /> {displayAddress}
                        </span>
                      ) : null}

                      <span className="ib_profile_meta_row">
                        <Calendar size={14} />
                        Joined {joinedDate || "—"}
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              <div className="ib_profile_dual_grid">
                <div className="ib_profile_base_card ib_profile_panel">
                  <h3 className="ib_profile_section_heading">
                    <div className="ib_profile_icon_square">
                      <User size={18} />
                    </div>
                    Personal Details
                  </h3>

                  <div className="ib_profile_data_point">
                    <label className="ib_profile_label_text">Full Name</label>
                    {isEditing ? (
                      <input
                        name="name"
                        value={formData.name || ""}
                        onChange={handleChange}
                      />
                    ) : (
                      <p className="ib_profile_value_text">{userData.name}</p>
                    )}
                  </div>

                  <div className="ib_profile_data_point">
                    <label className="ib_profile_label_text">
                      <Mail size={10} /> Email Address
                    </label>
                    <p className="ib_profile_value_text ib_profile_readonly">
                      {userData.email || userData.emailId}
                    </p>
                  </div>

                  <div className="ib_profile_data_point">
                    <label className="ib_profile_label_text">
                      <Phone size={10} /> Contact Number
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="mobile"
                        value={formData.mobile || formData.mobileNumber || ""}
                        maxLength={10}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                          setFormData((prev) => ({
                            ...prev,
                            mobile: value,
                            mobileNumber: value,
                          }));
                        }}
                      />
                    ) : (
                      <p className="ib_profile_value_text">
                        +91 {userData.mobile || userData.mobileNumber}
                      </p>
                    )}
                  </div>

                  <div className="ib_profile_data_point">
                    <label className="ib_profile_label_text">Company</label>
                    {isEditing ? (
                      <input
                        name="companyName"
                        value={formData.companyName || ""}
                        onChange={handleChange}
                      />
                    ) : (
                      <p className="ib_profile_value_text">
                        {userData.companyName || "—"}
                      </p>
                    )}
                  </div>

                  <div className="ib_profile_data_point">
                    <label className="ib_profile_label_text">GST</label>
                    {isEditing ? (
                      <input
                        name="gst"
                        value={formData.gst || ""}
                        onChange={handleChange}
                      />
                    ) : (
                      <p className="ib_profile_value_text">
                        {userData.gst || "—"}
                      </p>
                    )}
                  </div>

                  <div className="ib_profile_data_point">
                    <label className="ib_profile_label_text">Address</label>
                    {isEditing ? (
                      <textarea
                        name="address"
                        value={formData.address || formData.companyAddress || ""}
                        onChange={handleChange}
                      />
                    ) : (
                      <p className="ib_profile_value_text">
                        {displayAddress || "—"}
                      </p>
                    )}
                  </div>

                  <div className="ib_profile_data_point" style={{ marginBottom: 0 }}>
                    <label className="ib_profile_label_text">Reference</label>
                    {isEditing ? (
                      <input
                        name="reference"
                        value={formData.reference || ""}
                        onChange={handleChange}
                      />
                    ) : (
                      <p className="ib_profile_value_text">
                        {userData.reference || "—"}
                      </p>
                    )}
                  </div>
                </div>

                <div className="ib_profile_base_card ib_profile_panel">
                  <h3 className="ib_profile_section_heading">
                    <div className="ib_profile_icon_square">
                      <History size={18} />
                    </div>
                    Recent Voyages
                  </h3>

                  <div className="ib_profile_activity_stack">
                    {loading ? (
                      <p className="ib_profile_empty_voyages">Loading voyages...</p>
                    ) : voyages.length === 0 ? (
                      <p className="ib_profile_empty_voyages">
                        No bookings yet. Confirmed bookings will appear here.
                      </p>
                    ) : (
                      voyages.map((booking) => {
                        const isOpen = expandedVoyageId === booking.id;

                        return (
                        <div
                          key={booking.id}
                          className={`ib_profile_activity_row ${isOpen ? "is-open" : ""}`}
                          onClick={() =>
                            setExpandedVoyageId(isOpen ? "" : booking.id)
                          }
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              setExpandedVoyageId(isOpen ? "" : booking.id);
                            }
                          }}
                        >
                          <div className="ib_profile_activity_info">
                            <div className="ib_profile_activity_icon_bg">
                              <Anchor size={16} />
                            </div>
                            <div>
                              <p className="ib_profile_boat_name">{booking.boat}</p>
                              <p className="ib_profile_boat_details">
                                {[
                                  booking.date,
                                  booking.finalAmount
                                    ? `₹ ${Number(booking.finalAmount).toLocaleString("en-IN")}`
                                    : "",
                                ]
                                  .filter(Boolean)
                                  .join(" • ")}
                              </p>
                            </div>
                          </div>
                          <ChevronDown
                            size={18}
                            className={`ib_profile_voyage_chevron ${isOpen ? "open" : ""}`}
                          />

                          {isOpen ? (
                            <div
                              className="ib_profile_voyage_details"
                              onClick={(event) => event.stopPropagation()}
                            >
                              {booking.details.length > 0 ? (
                                booking.details.map((item, index) => (
                                  <div
                                    key={`${booking.id}-boat-${index}`}
                                    className="ib_profile_voyage_block"
                                  >
                                    <p className="ib_profile_voyage_block_title">
                                      {item.boatName || "Boat"}
                                    </p>
                                    {item.bookingDate ? (
                                      <p>Date: {formatDisplayDate(item.bookingDate)}</p>
                                    ) : null}
                                    {item.timeSlot ? (
                                      <p>Time: {item.timeSlot}</p>
                                    ) : null}
                                    {item.location ? (
                                      <p>Location: {item.location}</p>
                                    ) : null}
                                    {item.price ? (
                                      <p>
                                        Price: ₹{" "}
                                        {Number(item.price).toLocaleString("en-IN")}
                                      </p>
                                    ) : null}
                                  </div>
                                ))
                              ) : (
                                <p>No boat details saved for this booking.</p>
                              )}

                              {booking.addons.length > 0 ? (
                                <div className="ib_profile_voyage_block">
                                  <p className="ib_profile_voyage_block_title">
                                    Addons
                                  </p>
                                  {booking.addons.map((addon, index) => (
                                    <p key={`${booking.id}-addon-${index}`}>
                                      {addon.name}
                                      {addon.type ? ` (${addon.type})` : ""}
                                      {addon.quantity
                                        ? ` × ${addon.quantity}`
                                        : ""}
                                      {addon.price
                                        ? ` — ₹ ${Number(
                                            addon.price * (addon.quantity || 1)
                                          ).toLocaleString("en-IN")}`
                                        : ""}
                                    </p>
                                  ))}
                                </div>
                              ) : null}

                              <div className="ib_profile_voyage_block">
                                <p className="ib_profile_voyage_block_title">
                                  Payment
                                </p>
                                <p>
                                  Base: ₹{" "}
                                  {Number(booking.totalAmount).toLocaleString("en-IN")}
                                </p>
                                <p>
                                  Addons: ₹{" "}
                                  {Number(booking.addonTotal).toLocaleString("en-IN")}
                                </p>
                                <p>
                                  Tax: ₹{" "}
                                  {Number(booking.taxAmount).toLocaleString("en-IN")}
                                </p>
                                <p>
                                  Total: ₹{" "}
                                  {Number(booking.finalAmount).toLocaleString("en-IN")}
                                </p>
                                {booking.createdAt ? (
                                  <p>Booked on: {booking.createdAt}</p>
                                ) : null}
                              </div>
                            </div>
                          ) : null}
                        </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <header className="ib_profile_top_header">
              <div className="ib_profile_header_text_group">
                <h1 className="ib_profile_main_title">User Profile</h1>
                <p className="ib_profile_main_subtitle">
                  Sign in to view and manage your account.
                </p>
              </div>
            </header>
          )}
        </div>
      </div>

      <AuthPrompt
        open={showAuthPrompt}
        title="Login required"
        message="Please log in or sign up first to view your profile."
        primaryLabel="Log In"
        primaryPath="/login"
        secondaryLabel="Sign Up"
        secondaryPath="/signup"
        onClose={() => setShowAuthPrompt(false)}
      />

      <Footer />
    </>
  );
};

export default Profile;
